import { memo, useCallback, useMemo } from "react";
import styled from "styled-components";

import graphql from "babel-plugin-relay/macro";
import { useFragment } from "react-relay";

import { FieldMap$key } from "./__generated__/FieldMap.graphql";
import MapView, { MapFeature, MapZoomControl, MapMarker } from "./Map";
import { getCentroid } from "../utils";

interface MapProps {
  children?: any;
  hideMarker?: boolean;
  hideControls?: boolean;
}
export interface Props extends MapProps {
  field: FieldMap$key;
}

export default memo(function Comp(props: Props) {
  const data = useFragment(
    graphql`
      fragment FieldMap on FieldNode {
        geometry
      }
    `,
    props.field
  );
  return data.geometry !== null ? (
    <FieldMap
      children={props.children}
      hideMarker={props.hideMarker}
      hideControls={props.hideControls}
      geometry={data.geometry}
    />
  ) : null;
});

interface MapContainerProps extends MapProps {
  geometry: GeoJsonGeometry;
}

export const FieldMap = memo(function Comp(props: MapContainerProps) {
  const centroid: number[] | undefined = useMemo(
    () => getCentroid(props.geometry)?.reverse(),
    [props.geometry]
  );
  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      e.stopPropagation();
    },
    []
  );
  const bbox = useMemo(() => {
    const padding = 0.1;
    if (!props.geometry || !centroid) return undefined;
    return [
      centroid[0] - padding,
      centroid[1] - padding,
      centroid[0] + padding,
      centroid[1] + padding,
    ];
  }, [props.geometry, centroid]);
  return (
    <Container onClick={handleClick}>
      <MapView bbox={bbox} zoom={15} center={centroid}>
        {!props.hideControls ? <MapZoomControl /> : null}
        {!props.hideMarker ? <MapMarker point={centroid} /> : null}
        <MapFeature geometry={props.geometry} />
        {props.children}
      </MapView>
    </Container>
  );
});

export interface GeoJsonGeometry {
  type: string;
  coordinates?: number[][][];
  geometry?: Array<GeoJsonGeometry>;
}
/**
UI components
**/
const Container = styled.div`
  border-radius: 5px;
  height: 100%;
  width: 100%;
  overflow: hidden;
  filter: drop-shadow(0px 10px 15px rgba(168, 168, 168, 0.2));
`;
